import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If OAuth callback in progress, skip check (AuthCallback will handle it)
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, remember = false) => {
    const r = await api.post("/auth/login", { email, password, remember });
    setUser(r.data);
    return r.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data);
    return r.data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, loginWithGoogle, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function useRequireAuth(role) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true, state: { from: location.pathname } });
      return;
    }
    if (user.must_change_password && location.pathname !== "/change-password") {
      navigate("/change-password", { replace: true });
      return;
    }
    if (role && !checkRole(user.role, role)) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate, role, location]);
  return { user, loading };
}

function checkRole(userRole, required) {
  if (required === "staff") return userRole === "personale" || userRole === "admin";
  if (required === "admin") return userRole === "admin";
  return true;
}

export function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(", ");
  // If response body is HTML (e.g. Cloudflare error page), return a friendly generic message
  const rt = err?.response?.data;
  if (typeof rt === "string" && rt.length > 0 && rt.trim().startsWith("<")) {
    return "Servizio temporaneamente non disponibile. Riprova tra qualche istante.";
  }
  return err?.message || "Errore";
}
